import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Image from 'next/image';
import React from 'react';
import classNames from 'classnames';
import { useUpdateAttributes } from '../../../core/account/AuthServiceHooks';
import { Locale, LocaleFlagsMapping } from '../../../core/constants';
import cookie from '../../../core/helpers/cookie';
import SkipTranslationWrapper from '../SkipTranslationWrapper';
import MobileSidebarLayout from '../MobileSidebarLayout/MobileSidebarLayout';

const SidebarLanguageDropdown = () => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [updateAttributes] = useUpdateAttributes();
  const router = useRouter();

  const events = {
    onClickDropdown: () => {
      setShowDropdown(!showDropdown);
    },
    onClickLanguage: async (locale: string) => {
      const { pathname, asPath, query } = router as {
        pathname: string;
        asPath: string;
        query: Record<string, string>;
      };

      try {
        const attributes = [
          {
            Name: 'locale' as const,
            Value: locale,
          },
        ];
        await updateAttributes(attributes);
      } catch (error) {
        /* eslint-disable no-console */
        console.log('%cupdate attribute error:', 'color:yellow', error);
      }
      cookie.locale.set(locale as string);

      const params = new URLSearchParams(query as Record<string, string>);

      // Remove source and locale parameters as it was added by Google Hotel Ads to force redirect to certain locale.
      params.delete('source');
      params.delete('locale');
      router.push({ pathname, query: params.toString() }, asPath, { locale });
    },
  };

  return (
    <div className="w-full text-gray-600">
      <div
        className="py-3"
        onClick={events.onClickDropdown}
        onKeyDown={events.onClickDropdown}
        role="button"
        tabIndex={0}
      >
        <SkipTranslationWrapper className="flex items-center gap-4">
          <Image
            src={LocaleFlagsMapping[router.locale as string] as string}
            alt={LocaleFlagsMapping[router.locale as string] as string}
            width={24}
            height={24}
            className="cursor-pointer"
          />
          {t(`enum.Locale.${router.locale}`)}
        </SkipTranslationWrapper>
      </div>
      <MobileSidebarLayout visible={showDropdown} onClose={() => setShowDropdown(false)}>
        <section>
          {Object.values(Locale).map((value) => (
            <div
              key={value}
              className={classNames('flex rounded-lg py-3 px-2', {
                'bg-red-50 font-bold text-black': router.locale === value,
              })}
              onClick={() => events.onClickLanguage(value)}
              onKeyDown={() => events.onClickLanguage(value)}
              role="button"
              tabIndex={0}
            >
              <SkipTranslationWrapper className="flex items-center gap-4">
                <Image
                  src={LocaleFlagsMapping[value] as string}
                  alt={LocaleFlagsMapping[value] as string}
                  width={24}
                  height={24}
                  className="cursor-pointer"
                />
                {t(`enum.Locale.${value}`)}
              </SkipTranslationWrapper>
            </div>
          ))}
        </section>
      </MobileSidebarLayout>
    </div>
  );
};

export default SidebarLanguageDropdown;
